import ContextReminderContainer from '@app/components/ContextReminder'
import { CoverPhoto, CoverPhotoOverlay } from '@app/components/CoverPhoto'
import DeleteConfirmationModal from '@app/components/DeleteConfirmationModal'
import ExpirationIcon from '@app/components/ExpirationIcon'
import { actionCreators as FeedItemActionCreators } from '@app/components/FeedItem/modules/feedItems'
import { NavTabItem, NavTabs } from '@app/components/NavTabs'
import {
  ListingShareType,
  shareAttachmentActionCreators,
} from '@app/components/ShareAttachmentModal/modules/shareAttachment'
import { ImageSize } from '@app/models/enums'
import { ICurrentUserState } from '@app/store/modules/account'
import { bindFunctions } from '@app/utils/class'
import {
  formatAddressSingleLine,
  formatListingPrice,
  formatListingSize,
  humanize,
} from '@app/utils/formatting'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as React from 'react'
import DocumentTitle from 'react-document-title'
import { Route, RouteComponentProps, Switch } from 'react-router-dom'
import {
  Alert,
  Badge,
  Card,
  CardHeader,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from 'reactstrap'
import { actionCreators, IListingState } from '../modules/listing'
import ListingActivity from './ListingActivity'
import ListingDetails from './ListingDetails'
import ListingFiles from './ListingFiles'
import ListingLeads from './ListingLeads'
import ListingProgress from './ListingProgress'
import { ListingProgressV2 } from './ListingProgress2'

export type ListingPageProps = IListingState &
  typeof actionCreators &
  RouteComponentProps<{ id: string }> &
  ICurrentUserState &
  typeof shareAttachmentActionCreators &
  typeof FeedItemActionCreators

export default class ListingPage extends React.PureComponent<ListingPageProps> {
  constructor(props: ListingPageProps) {
    super(props)
    bindFunctions(this)
  }

  public componentDidMount() {
    this.props.loadListing(this.props.match.params.id)
  }

  public componentDidUpdate(prevProps: ListingPageProps) {
    if (prevProps.match.params.id !== this.props.match.params.id) {
      this.props.loadListing(this.props.match.params.id)
    }
  }

  public componentWillUnmount() {
    this.props.resetState()
  }

  public render() {
    if (!this.props.listing.id) {
      return (
        <DocumentTitle title={'View Listing'}>
          <Container className='my-3'>
            <div className='mb-3 text-center'>
              Retrieving Listing Information
            </div>
            {this.props.error && (
              <Alert color='danger'>{this.props.error}</Alert>
            )}
          </Container>
        </DocumentTitle>
      )
    }

    return (
      <DocumentTitle title={this.props.listing.title}>
        <Container className='my-3'>
          {this._renderHeader()}
          {this._renderReferralAlert()}
          {this._renderMarketingPackageAndFlyer()}
          {this._renderTabs()}
          <Switch>
            <Route
              path={`${this.props.match.path}/details`}
              render={() => <ListingDetails {...this.props} />}
            />
            <Route
              path={`${this.props.match.path}/leads`}
              render={() => <ListingLeads {...this.props} />}
            />
            <Route
              path={`${this.props.match.path}/progress`}
              render={() => <ListingProgressV2 {...this.props} />}
            />
            <Route
              path={`${this.props.match.path}/files`}
              render={() => <ListingFiles {...this.props} />}
            />
            <Route
              path={`${this.props.match.path}/reminders`}
              render={() => (
                <ContextReminderContainer
                  contextId={this.props.listing.id}
                  {...this.props}
                />
              )}
            />
            <Route
              path={this.props.match.path}
              render={() => <ListingActivity {...this.props} />}
            />
          </Switch>
          <DeleteConfirmationModal
            isOpen={this.props.showConfirmDelete}
            itemName={this.props.listing.title}
            onConfirm={() => this.props.deleteListing(this.props.listing.id)}
            onToggleModal={() => this.props.toggleConfirmDelete(false)}
          />
        </Container>
      </DocumentTitle>
    )
  }

  private _getCoverPhotoUrl(): string {
    if (this.props.listing.thumbnailImage) {
      return this.props.listing.thumbnailImage.getImageUrl(ImageSize.original)
    } else {
      return `/images/default-property.png`
    }
  }

  private _renderHeader() {
    return (
      <Card>
        <CardHeader className='bg-secondary text-white d-flex justify-content-between'>
          <div>
            <FontAwesomeIcon icon={['fal', 'sign']} fixedWidth /> Listing
            <ExpirationIcon
              className='ml-2'
              date={this.props.listing.expirationDate}
            />
          </div>
          <div>
            <UncontrolledDropdown className='d-inline-block'>
              <DropdownToggle tag='a' className='px-1 text-center'>
                <FontAwesomeIcon icon={['fal', 'chevron-down']} fixedWidth />
              </DropdownToggle>
              <DropdownMenu right>
                <DropdownItem
                  onClick={() =>
                    this.props.history.push(
                      `/properties/property/${this.props.listing.property.id}`,
                    )
                  }
                >
                  Go to property
                </DropdownItem>
                {this.props.permissions.canEdit && (
                  <DropdownItem
                    onClick={() =>
                      this.props.history.push(
                        `/brokerage/pipeline/new?listingId=${this.props.listing.id}`,
                      )
                    }
                  >
                    Add to Pipeline
                  </DropdownItem>
                )}
                {(this.props.permissions.canEdit ||
                  this.props.permissions.canDelete ||
                  this._canManage()) && <DropdownItem divider />}
                {this._canManage() && (
                  <DropdownItem
                    onClick={() =>
                      window.alert(
                        'Manage Listing access is not ready yet. Check back soon!',
                      )
                    }
                  >
                    Manage
                  </DropdownItem>
                )}
                {this.props.permissions.canEdit && (
                  <DropdownItem
                    onClick={() =>
                      this.props.history.push(
                        `/brokerage/listing/${this.props.listing.id}/edit`,
                      )
                    }
                  >
                    Edit
                  </DropdownItem>
                )}
                {this.props.permissions.canDelete && (
                  <DropdownItem
                    disabled={this.props.isDeleting}
                    onClick={() => this.props.toggleConfirmDelete(true)}
                  >
                    Delete
                  </DropdownItem>
                )}
              </DropdownMenu>
            </UncontrolledDropdown>
          </div>
        </CardHeader>
        <CoverPhoto photoUrl={this._getCoverPhotoUrl()}>
          <CoverPhotoOverlay>
            <div className='d-md-flex justify-content-between align-items-end'>
              <div>
                <div className='font-weight-bold'>
                  {this.props.listing.title}
                </div>
                <div>
                  {formatAddressSingleLine(this.props.listing.property.address)}
                </div>
                <div>
                  {formatListingSize(this.props.listing)} |{' '}
                  {humanize(
                    this.props.listing.property.propertyTypes.join(','),
                  )}
                  <span className='d-md-none'>
                    {' '}
                    | {humanize(this.props.listing.type)} |{' '}
                    {formatListingPrice(this.props.listing)}
                  </span>
                </div>
              </div>
              <div className='d-none d-md-block text-right'>
                <div>{humanize(this.props.listing.type)}</div>
                <div>{formatListingPrice(this.props.listing)}</div>
              </div>
            </div>
          </CoverPhotoOverlay>
        </CoverPhoto>
      </Card>
    )
  }

  private _canManage() {
    return (
      this.props.user !== undefined &&
      (this.props.user.permissions.isAdmin === true ||
        this.props.user.id === this.props.listing.creator)
    )
  }

  private _onShareFlyer() {
    if (this.props.listing.flyer) {
      this.props.showShareListingAttachmentModal(
        this.props.listing.flyer,
        this.props.listing,
        ListingShareType.Flyer,
      )
    }
  }

  private _onShareMarketingPackage() {
    if (this.props.listing.marketingPackage) {
      this.props.showShareListingAttachmentModal(
        this.props.listing.marketingPackage,
        this.props.listing,
        ListingShareType.MarketingPackage,
      )
    }
  }

  private _renderReferralAlert() {
    if (!this.props.listing.referral) {
      return null
    }

    return (
      <div className='mt-3'>
        <Badge color='primary' className='text-white' style={{ fontSize: '0.9rem' }}>Referral</Badge>
      </div>
    )
  }

  private _renderMarketingPackageAndFlyer() {
    if (!this.props.listing.flyer && !this.props.listing.marketingPackage) {
      return null
    }

    return (
      <div className='mt-3'>
        {this.props.listing.flyer !== undefined && (
          <span className='mr-3'>
            Flyer
            <a
              className='ml-2'
              href={`/api/Files/${this.props.listing.flyerId}/Download`}
              title='Download'
            >
              <FontAwesomeIcon icon={['fal', 'download']} fixedWidth />
            </a>
            <a
              className='ml-2 text-primary'
              onClick={this._onShareFlyer}
              title='Share'
            >
              <FontAwesomeIcon icon={['fal', 'share-alt']} fixedWidth />
            </a>
          </span>
        )}
        {this.props.listing.marketingPackage && (
          <span className='mr-3'>
            Marketing Package
            <a
              className='ml-2'
              href={`/api/Files/${this.props.listing.marketingPackageId}/Download`}
              title='Download'
            >
              <FontAwesomeIcon icon={['fal', 'download']} fixedWidth />
            </a>
            <a
              className='ml-2 text-primary'
              onClick={this._onShareMarketingPackage}
              title='Share'
            >
              <FontAwesomeIcon icon={['fal', 'share-alt']} fixedWidth />
            </a>
          </span>
        )}
      </div>
    )
  }

  private _renderTabs() {
    return (
      <div className='my-3'>
        <NavTabs>
          <NavTabItem
            exact
            iconName='chart-line'
            label='Activity'
            to={this.props.match.url}
          />
          <NavTabItem
            iconName='list'
            label='Details'
            to={`${this.props.match.url}/details`}
          />
          <NavTabItem
            badgeCount={this.props.listing.property.leads.size}
            iconName='sticky-note'
            label='Leads'
            to={`${this.props.match.url}/leads`}
          />
          <NavTabItem
            badgeCount={this.props.listing.progressV2.percentageComplete}
            badgeSuffix='%'
            iconName='clipboard-list'
            label='Progress'
            to={`${this.props.match.url}/progress`}
          />
          <NavTabItem
            badgeCount={this.props.listing.property.attachments.size}
            iconName='paperclip'
            label='Files'
            to={`${this.props.match.url}/files`}
          />
          <NavTabItem
            iconName='bell'
            label='Reminders'
            to={`${this.props.match.url}/reminders`}
          />
        </NavTabs>
      </div>
    )
  }
}
